"use client";

import Link from "next/link";
import { useState } from "react";
import {
  ArrowUpRight,
  Bot,
  CirclePlus,
  Globe,
  MessageSquare,
  MoreHorizontal,
  Search,
  Sparkles,
  X,
} from "lucide-react";

const initialAssistants = [
  {
    id: "1",
    name: "Hotel Guest Assistant",
    description: "Answers guest questions, explains hotel services, and assists with booking inquiries.",
    type: "Hospitality",
    channels: ["Website"],
    status: "Active",
    conversations: 1248,
  },
  {
    id: "2",
    name: "Customer Support Assistant",
    description: "Handles common customer questions and support requests.",
    type: "Customer Support",
    channels: ["Website"],
    status: "Draft",
    conversations: 0,
  },
  {
    id: "3",
    name: "Booking Verification Agent",
    description: "Assists with reservation checks and booking verification workflows.",
    type: "Booking",
    channels: ["Internal"],
    status: "Active",
    conversations: 386,
  },
];

export default function AssistantsPage() {
  const [assistants, setAssistants] = useState(initialAssistants);
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState("All");
  const [showCreate, setShowCreate] = useState(false);
  const [newName, setNewName] = useState("");

  const filteredAssistants = assistants.filter((assistant) => {
    const matchesSearch =
      assistant.name.toLowerCase().includes(search.toLowerCase()) ||
      assistant.description.toLowerCase().includes(search.toLowerCase());

    const matchesFilter =
      filter === "All" || assistant.status === filter;

    return matchesSearch && matchesFilter;
  });

  function createAssistant() {
    if (!newName.trim()) return;

    const newAssistant = {
      id: String(Date.now()),
      name: newName.trim(),
      description: "New AI assistant. Configure its instructions and settings.",
      type: "General",
      channels: ["Not connected"],
      status: "Draft",
      conversations: 0,
    };

    setAssistants((current) => [...current, newAssistant]);
    setNewName("");
    setShowCreate(false);
  }

  return (
    <main className="min-h-screen bg-slate-50 p-6 md:p-10">
      <div className="mx-auto max-w-7xl">

        <div className="mb-8 flex flex-wrap items-center justify-between gap-4">
          <div>
            <div className="mb-2 flex items-center gap-2 text-sm text-slate-500">
              <Link href="/" className="hover:text-blue-600">
                Dashboard
              </Link>
              <span>/</span>
              <span className="text-slate-700">AI Assistants</span>
            </div>

            <h1 className="text-3xl font-bold tracking-tight text-slate-900">
              AI Assistant Studio
            </h1>

            <p className="mt-2 text-slate-500">
              Create, configure, and manage your business AI assistants.
            </p>
          </div>

          <button
            onClick={() => setShowCreate(true)}
            className="flex items-center gap-2 rounded-lg bg-blue-600 px-5 py-3 text-sm font-semibold text-white shadow-sm hover:bg-blue-700"
          >
            <CirclePlus size={18} />
            Create Assistant
          </button>
        </div>

        <div className="mb-8 grid gap-4 sm:grid-cols-3">
          <div className="rounded-xl border border-slate-200 bg-white p-5">
            <div className="flex items-center justify-between">
              <p className="text-sm text-slate-500">Total Assistants</p>
              <Bot className="text-blue-600" size={20} />
            </div>
            <p className="mt-3 text-3xl font-bold text-slate-900">
              {assistants.length}
            </p>
          </div>

          <div className="rounded-xl border border-slate-200 bg-white p-5">
            <div className="flex items-center justify-between">
              <p className="text-sm text-slate-500">Active Assistants</p>
              <Sparkles className="text-emerald-600" size={20} />
            </div>
            <p className="mt-3 text-3xl font-bold text-slate-900">
              {assistants.filter((a) => a.status === "Active").length}
            </p>
          </div>

          <div className="rounded-xl border border-slate-200 bg-white p-5">
            <div className="flex items-center justify-between">
              <p className="text-sm text-slate-500">Total Conversations</p>
              <MessageSquare className="text-violet-600" size={20} />
            </div>
            <p className="mt-3 text-3xl font-bold text-slate-900">
              {assistants
                .reduce((total, a) => total + a.conversations, 0)
                .toLocaleString()}
            </p>
          </div>
        </div>

        <div className="mb-5 flex flex-col justify-between gap-4 sm:flex-row">
          <div className="relative w-full sm:max-w-md">
            <Search
              className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
              size={18}
            />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search assistants..."
              className="w-full rounded-lg border border-slate-200 bg-white py-3 pl-10 pr-4 text-sm outline-none focus:border-blue-500"
            />
          </div>

          <div className="flex gap-2">
            {["All", "Active", "Draft"].map((item) => (
              <button
                key={item}
                onClick={() => setFilter(item)}
                className={`rounded-lg px-4 py-2 text-sm font-medium ${
                  filter === item
                    ? "bg-slate-900 text-white"
                    : "border border-slate-200 bg-white text-slate-600 hover:bg-slate-100"
                }`}
              >
                {item}
              </button>
            ))}
          </div>
        </div>

        <div className="grid gap-5 md:grid-cols-2 xl:grid-cols-3">
          {filteredAssistants.map((assistant) => (
            <div
              key={assistant.id}
              className="rounded-xl border border-slate-200 bg-white p-6 shadow-sm transition hover:-translate-y-0.5 hover:shadow-md"
            >
              <div className="mb-5 flex items-start justify-between">
                <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-blue-50 text-blue-600">
                  <Bot size={25} />
                </div>

                <div className="flex items-center gap-2">
                  <span
                    className={`rounded-full px-3 py-1 text-xs font-semibold ${
                      assistant.status === "Active"
                        ? "bg-emerald-50 text-emerald-700"
                        : "bg-amber-50 text-amber-700"
                    }`}
                  >
                    {assistant.status}
                  </span>
                  <MoreHorizontal size={19} className="text-slate-400" />
                </div>
              </div>

              <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-blue-600">
                {assistant.type}
              </p>

              <h2 className="text-lg font-bold text-slate-900">
                {assistant.name}
              </h2>

              <p className="mt-2 min-h-12 text-sm leading-6 text-slate-500">
                {assistant.description}
              </p>

              <div className="my-5 border-t border-slate-100" />

              <div className="mb-5 flex items-center justify-between text-sm">
                <span className="flex items-center gap-2 text-slate-500">
                  <Globe size={16} />
                  {assistant.channels.join(", ")}
                </span>

                <span className="font-medium text-slate-700">
                  {assistant.conversations.toLocaleString()} chats
                </span>
              </div>

              <Link
                href={`/assistants/${assistant.id}`}
                className="flex w-full items-center justify-center gap-2 rounded-lg border border-slate-200 px-4 py-3 text-sm font-semibold text-slate-700 hover:border-blue-300 hover:bg-blue-50 hover:text-blue-700"
              >
                Configure Assistant
                <ArrowUpRight size={16} />
              </Link>
            </div>
          ))}

          {filteredAssistants.length === 0 && (
            <div className="col-span-full rounded-xl border border-dashed border-slate-300 bg-white p-12 text-center">
              <Bot className="mx-auto mb-3 text-slate-400" size={32} />
              <p className="font-semibold text-slate-800">
                No assistants found
              </p>
              <p className="mt-1 text-sm text-slate-500">
                Try another search or create a new assistant.
              </p>
            </div>
          )}
        </div>
      </div>

      {showCreate && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/50 p-4">
          <div className="w-full max-w-md rounded-2xl bg-white p-6 shadow-xl">
            <div className="mb-5 flex items-center justify-between">
              <h2 className="text-xl font-bold text-slate-900">
                Create AI Assistant
              </h2>
              <button
                onClick={() => setShowCreate(false)}
                className="rounded-lg p-2 text-slate-400 hover:bg-slate-100"
                aria-label="Close"
              >
                <X size={20} />
              </button>
            </div>

            <label className="mb-2 block text-sm font-medium text-slate-700">
              Assistant name
            </label>

            <input
              autoFocus
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") createAssistant();
              }}
              placeholder="e.g. Resort Guest Assistant"
              className="w-full rounded-lg border border-slate-200 px-4 py-3 text-sm outline-none focus:border-blue-500"
            />

            <div className="mt-6 flex justify-end gap-3">
              <button
                onClick={() => setShowCreate(false)}
                className="rounded-lg border border-slate-200 px-4 py-2 text-sm font-medium text-slate-600 hover:bg-slate-50"
              >
                Cancel
              </button>

              <button
                onClick={createAssistant}
                disabled={!newName.trim()}
                className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50"
              >
                Create Assistant
              </button>
            </div>
          </div>
        </div>
      )}
    </main>
  );
}
